// stores/useMenuStore.ts
import { defineStore } from 'pinia';
import { useApi } from '~/composables/useApi';
import { LOGIN_STORAGE_KEYS } from '~/composables/useLoginSuccess';
import {
  getMenuTabsConfig,
  getMenuIcon,
  filterTabsByAuthority,
  filterTabsByService,
  normalizeMenuRoutePath,
  type ViewType,
  DEFAULT_VIEW_TYPE,
  MENU_ICONS_CONFIG,
} from '~/config/tabMenuConfig';

/**
 * iframe에서 제외할 경로 목록 (블랙리스트 방식)
 * /screens/* 경로는 기본적으로 모두 iframe으로 렌더링되며,
 * 아래 경로들만 일반 페이지로 렌더링됩니다.
 */
const iframeExcludedPaths = [
  // Task 페이지 (Nuxt로 구현된 페이지)
  '/task/',
  '/pa/',

  // Component 페이지
  '/components/',

  // 로그인 페이지
  '/login',
  '/login.do',

  // 특정 Common 페이지
  '/screens/common/login',
  '/screens/common/modal',
  '/screens/common/test',
  '/guide',
];

/**
 * 하드코딩된 탭 설정을 적용하여 탭 정보 생성
 * @param menuItem 메뉴 아이템 (서버에서 받은 원본)
 * @param userAuthorities 사용자 권한 목록 (탭 필터링에 사용)
 * @returns 탭 정보 배열
 */
const applyHardcodedTabs = (
  menuItem: any,
  userAuthorities: string[] = [],
  serviceTypes: string[] = [],
): any[] => {
  const menuPath = menuItem.path || '';
  const tabConfig =
    getMenuTabsConfig(menuPath) ||
    getMenuTabsConfig(menuItem.pgmId);

  if (tabConfig) {
    // 권한 → 구독 서비스 순으로 탭 필터링
    const filteredTabs = filterTabsByService(
      filterTabsByAuthority(tabConfig.tabs, userAuthorities),
      serviceTypes,
    );
    return filteredTabs.map(tab => ({
      id: tab.id,
      labelKey: tab.labelKey,
      path: tab.path || normalizeMenuRoutePath(menuPath) || '',
    }));
  }

  // API에서 tabs를 보내는 경우 — path가 없으면 하드코딩 탭으로 대체
  if (menuItem.tabs && Array.isArray(menuItem.tabs) && menuItem.tabs.length > 0) {
    const hasRoutableTab = menuItem.tabs.some((tab: { path?: string }) => Boolean(String(tab?.path ?? '').trim()));
    if (!hasRoutableTab) {
      const fallbackConfig = getMenuTabsConfig(normalizeMenuRoutePath(menuPath)) || getMenuTabsConfig(menuItem.pgmId);
      if (fallbackConfig) {
        const filteredTabs = filterTabsByService(
          filterTabsByAuthority(fallbackConfig.tabs, userAuthorities),
          serviceTypes,
        );
        return filteredTabs.map(tab => ({
          id: tab.id,
          labelKey: tab.labelKey,
          path: tab.path || '',
        }));
      }
    }

    return menuItem.tabs.map((tab: any) => ({
      id: tab.id,
      label: tab.label,
      labelKey: tab.labelKey,
      path: tab.path ? normalizeMenuRoutePath(tab.path) : tab.path || normalizeMenuRoutePath(menuPath) || '',
    }));
  }

  return [];
};

// 기본 아이콘 (아이콘을 찾지 못할 때 사용)
const DEFAULT_MENU_ICON = 'default-icon';

/**
 * 1뎁스 메뉴 아이콘 적용 (하드코딩 우선, 서버 값 fallback, 기본 아이콘)
 * - ASIS에서 내려주는 전체 URL/경로(예: /app/resources/...)는 사용하지 않고 기본 아이콘으로 대체 (TOBE에서 경로 깨짐 방지)
 * @param menuItem 메뉴 아이템
 * @returns 아이콘 이름(경로 아님, 예: dashboard-icon)
 */
const applyMenuIcon = (menuItem: any): string => {
  // 1. 하드코딩된 아이콘 우선
  const hardcodedIcon = getMenuIcon(menuItem.pgmId);
  if (hardcodedIcon) {
    return hardcodedIcon;
  }
  // 2. 서버에서 받은 아이콘 - URL/절대경로가 아닐 때만 사용 (ASIS 경로는 TOBE에서 깨지므로 제외)
  if (menuItem.icon && typeof menuItem.icon === 'string') {
    const trimmed = menuItem.icon.trim();
    const isUrlOrPath = trimmed.startsWith('http') || (trimmed.startsWith('/') && trimmed.length > 1);
    if (!isUrlOrPath) {
      return trimmed;
    }
  }
  // 3. 기본 아이콘
  return DEFAULT_MENU_ICON;
};

// Transform server menu data to client structure
const transformMenuData = (
  serverMenuData: any[],
  userAuthorities: string[] = [],
  serviceTypes: string[] = [],
) => {
  const levels: Record<string, string[]> = {};
  serverMenuData.forEach(item => {
    const level = item.levelId;
    if (!levels[level]) levels[level] = [];
    levels[level].push(item.pgmNm);
  });

  // Process levelId 2 as main menus (서버 menuList 원본 순서 유지)
  const mainMenus = serverMenuData.filter(item => item.levelId === '2');

  const result: any[] = [];

  mainMenus.forEach(mainMenu => {
    const mainMenuStructure = {
      title: mainMenu.pgmNm,
      pgmId: mainMenu.pgmId,
      icon: applyMenuIcon(mainMenu), // 하드코딩된 아이콘 적용
      expanded: false,
      items: [] as any[],
      submenus: [] as any[],
      _isServerMenu: true,
    };

    // Process levelId 3 as submenus (서버 menuList 원본 순서 유지)
    const level3Menus = serverMenuData.filter(
      item =>
        item.levelId === '3' &&
        item.fullPgmId.startsWith(mainMenu.fullPgmId + '/') &&
        item.fullPgmId.split('/').length === 4,
    );

    level3Menus.forEach(level3Menu => {
      // Check for level 4 items
      const level4Items = serverMenuData.filter(
        item =>
          item.levelId === '4' &&
          item.fullPgmId.startsWith(level3Menu.fullPgmId + '/') &&
          item.fullPgmId.split('/').length === 5,
      );

      // Create submenu structure if level 4 items exist
      if (level4Items.length > 0) {
        const submenuStructure = {
          title: level3Menu.pgmNm,
          items: [] as any[],
        };

        level4Items.forEach(level4Item => {
          // 하드코딩된 탭 설정 적용
          const tabs = applyHardcodedTabs(level4Item, userAuthorities, serviceTypes);

          const menuItem = {
            name: level4Item.pgmNm,
            path: level4Item.path || '#',
            icon: level4Item.icon || '',
            pgmId: level4Item.pgmId,
            manualUrl: level4Item.newManualUrl || '',
            linkUrl: level4Item.linkUrl  || '',
            tabs: tabs,
            disabled: !level4Item.path,
            // viewType: 'A' = ASIS (iframe), 'T' = TOBE (Vue) - 기본값 'A'
            viewType: (level4Item.viewType || DEFAULT_VIEW_TYPE) as ViewType,
          };

          submenuStructure.items.push(menuItem);
        });

        if (submenuStructure.items.length > 0) {
          mainMenuStructure.submenus.push(submenuStructure);
        }
      }
      // Level 3 is a leaf node — L4 없이 바로 화면으로 가는 중메뉴: 순서대로 독립 블록, 제목 클릭으로 이동 (하단 중복 링크 숨김은 TheSidebar)
      else {
        const tabs = applyHardcodedTabs(level3Menu, userAuthorities, serviceTypes);

        const menuItem = {
          name: level3Menu.pgmNm,
          path: level3Menu.path || '#',
          icon: level3Menu.icon || '',
          pgmId: level3Menu.pgmId,
          manualUrl: level3Menu.newManualUrl || '',
          linkUrl: level3Menu.linkUrl || level3Menu.LINK_URL || '',
          tabs: tabs,
          disabled: !level3Menu.path,
          viewType: (level3Menu.viewType || DEFAULT_VIEW_TYPE) as ViewType,
        };

        mainMenuStructure.submenus.push({
          title: level3Menu.pgmNm,
          items: [menuItem],
          navigateByTitleOnly: true,
        });
      }
    });

    // Add main menu if has submenus or items
    if (mainMenuStructure.submenus.length > 0 || mainMenuStructure.items.length > 0) {
      result.push(mainMenuStructure);
    }
  });

  return result;
};

export const useMenuStore = defineStore('menu', {
  state: () => ({
    /**
     * 서버에서 받은 메뉴 원본 데이터 (탭 필터링 이전).
     * 탭 목록(menuSections getter)은 이 값과 serviceTypes/menuUserAuthorities 를
     * 조합해 접근할 때마다 다시 계산한다 — setMenuList 호출 시점의 serviceTypes 값으로
     * 한 번만 구워서 저장하면, 그 이후 serviceTypes 가 바뀌어도(로그인 직후 비동기 응답
     * 순서가 꼬이는 경우 등) 탭이 재계산되지 않아 사라진 채로 고정되는 버그가 있었다.
     */
    rawMenuData: [] as any[],
    menuUserAuthorities: [] as string[],
    /**
     * 회사가 구독 중인 서비스 코드 (03: 개인성과 / 04: 인사평가 / 05: 업무).
     *
     * 옵션 인자(userAuthorities)와 달리 state 로 두는 이유:
     * 새로고침 경로인 middleware/auth-check.global.ts 가 loadMenuFromSession() 을
     * 인자 없이 호출한다. 인자로 받으면 그 경로에서 빈 배열이 되어 조건이 걸린 탭이
     * 통째로 사라진다. state + localStorage 라면 새로고침에도 살아남는다.
     */
    serviceTypes: [] as string[],
  }),

  getters: {
    // rawMenuData/serviceTypes/menuUserAuthorities 가 바뀔 때마다 다시 계산되는 탭 목록
    menuSections(state): any[] {
      return transformMenuData(state.rawMenuData, state.menuUserAuthorities, state.serviceTypes);
    },

    // Current menu data
    sections(): any[] {
      return this.menuSections;
    },

    // Check if path should use iframe (블랙리스트 방식)
    // 참고: viewType이 있는 경우 이 함수는 사용되지 않음 (layouts/default.vue 참고)
    isIframePath:
      () =>
      (path: string): boolean => {
        // 제외 목록 체크
        for (const excludedPath of iframeExcludedPaths) {
          // startsWith로 끝나는 경로는 prefix 매칭
          if (excludedPath.endsWith('/')) {
            if (path.startsWith(excludedPath)) {
              return false;
            }
          }
          // 정확히 일치하는 경로
          else if (path === excludedPath) {
            return false;
          }
        }

        // 제외 목록에 없으면 iframe 사용
        return true;
      },

    // Check if user has permission to access path
    hasPathPermission(): (path: string) => boolean {
      return (path: string): boolean => {
        if (!path) return false;

        // Always allow common paths
        const commonAllowedPaths = [
          '/screens/common/login',
          '/screens/common/modal',
          '/screens/common/test',
          '/login',
          '/error',
          '/',
        ];

        if (commonAllowedPaths.includes(path)) {
          return true;
        }

        const menuSections = this.menuSections;

        // Allow if menu not loaded yet
        if (!menuSections || menuSections.length === 0) {
          return true;
        }

        // Check if path exists in menu data
        const findPathInMenus = (menus: any[], targetPath: string): boolean => {
          for (const menu of menus) {
            if (menu.path === targetPath) return true;

            if (menu.tabs && Array.isArray(menu.tabs)) {
              for (const tab of menu.tabs) {
                if (tab.path === targetPath) return true;
              }
            }

            if (menu.submenus && Array.isArray(menu.submenus)) {
              for (const submenu of menu.submenus) {
                if (submenu.items && Array.isArray(submenu.items)) {
                  for (const item of submenu.items) {
                    if (item.path === targetPath) return true;

                    if (item.tabs && Array.isArray(item.tabs)) {
                      for (const tab of item.tabs) {
                        if (tab.path === targetPath) return true;
                      }
                    }
                  }
                }
              }
            }

            if (menu.items && Array.isArray(menu.items)) {
              for (const item of menu.items) {
                if (item.path === targetPath) return true;

                if (item.tabs && Array.isArray(item.tabs)) {
                  for (const tab of item.tabs) {
                    if (tab.path === targetPath) return true;
                  }
                }
              }
            }
          }
          return false;
        };

        return findPathInMenus(menuSections, path);
      };
    },
  },

  actions: {
    /**
     * 언어 변경 후 세션 메뉴 재조회 (레거시 menuList_ajax.do)
     * selectUserInfo.do?lang= 호출 직후 CustomLocaleChangeInterceptor가 세션 menuList를 갱신함
     */
    async refreshMenuList(options?: { userAuthorities?: string[] }) {
      try {
        const response = await useApi('/api/common/menuList_ajax.do', {
          method: 'GET',
          skipLoading: true,
        });

        const menuList = response?.menuList;
        if (!menuList || !Array.isArray(menuList) || menuList.length === 0) {
          return false;
        }

        const normalized = menuList.map((item: Record<string, unknown>) => ({
          ...item,
          path: item.path || item.url || '',
        }));

        this.setMenuList(normalized, options);
        return true;
      } catch (error) {
        console.error('Failed to refresh menu list:', error);
        return false;
      }
    },

    /**
     * 서버에서 받은 메뉴 데이터를 설정
     * @param menuListJson 메뉴 JSON 데이터
     * @param options 옵션 (userAuthorities: 사용자 권한 목록)
     */
    setMenuList(
      menuListJson: any,
      options?: { userAuthorities?: string[]; loginUserKey?: string },
    ) {
      // Return if no data
      if (!menuListJson) {
        return;
      }

      let parsedData = menuListJson;

      // Parse if string
      if (typeof menuListJson === 'string') {
        try {
          parsedData = JSON.parse(menuListJson);
        } catch (error) {
          console.error('JSON parsing failed:', error);
          return;
        }
      }

      if (parsedData && Array.isArray(parsedData) && parsedData.length > 0) {
        // 구독 서비스는 state 에서 읽는다 (새로고침 경로가 옵션을 넘기지 않으므로)
        this.restoreServiceTypes();
        // 사용자 권한 정보를 전달하여 탭 필터링에 사용 (옵션이 없으면 기존 값 유지)
        if (options?.userAuthorities) {
          this.menuUserAuthorities = options.userAuthorities;
        }
        // 원본 데이터만 저장한다 — 탭 필터링(menuSections getter)은 매번 다시 계산되므로
        // 이 시점의 serviceTypes 가 아직 틀려도 이후 setServiceTypes 가 반영되면 자동으로 고쳐진다
        this.rawMenuData = parsedData;

        // Save to localStorage for persistence across tabs
        if (import.meta.client) {
          localStorage.setItem(LOGIN_STORAGE_KEYS.menuListJson, JSON.stringify(parsedData));
          if (options?.loginUserKey) {
            localStorage.setItem(LOGIN_STORAGE_KEYS.loginUserKey, options.loginUserKey);
          }
        }

        console.log('Menu store updated. Menu count:', this.menuSections.length);
      } else {
        console.log('Invalid menu data. Keeping default menu:', parsedData);
      }
    },

    /**
     * 구독 서비스 목록 설정 — 로그인 직후 1회. 메뉴가 이미 만들어져 있으면 탭을 다시 계산한다.
     * @param serviceTypes ['01','03','05', ...]
     */
    setServiceTypes(serviceTypes: string[]) {
      this.serviceTypes = Array.isArray(serviceTypes) ? serviceTypes : [];
      if (import.meta.client) {
        localStorage.setItem('menuServiceTypes', JSON.stringify(this.serviceTypes));
      }
    },

    /** localStorage 에 남은 구독 목록을 state 로 되살린다 (state 가 비어 있을 때만) */
    restoreServiceTypes() {
      if (!import.meta.client || this.serviceTypes.length > 0) return;
      try {
        const saved = localStorage.getItem('menuServiceTypes');
        if (saved) this.serviceTypes = JSON.parse(saved) || [];
      } catch (error) {
        console.error('구독 서비스 목록 복원 실패:', error);
        this.serviceTypes = [];
      }
    },

    /**
     * localStorage에서 메뉴 복원 (탭/페이지 새로고침 시)
     * @param options 옵션 (userAuthorities: 사용자 권한 목록)
     */
    loadMenuFromSession(options?: { userAuthorities?: string[]; loginUserKey?: string }) {
      if (import.meta.client) {
        const savedMenuJson = localStorage.getItem(LOGIN_STORAGE_KEYS.menuListJson);
        if (savedMenuJson) {
          try {
            const parsedData = JSON.parse(savedMenuJson);
            this.setMenuList(parsedData, options);
            return true;
          } catch (error) {
            console.error('Failed to load menu from localStorage:', error);
            return false;
          }
        }
      }
      return false;
    },

    /**
     * 서버 세션에서 메뉴 조회 (AS-IS 세션 기반 메뉴 복구)
     */
    async fetchMenuFromSession(options?: { userAuthorities?: string[]; loginUserKey?: string }) {
      try {
        const res: any = await useApi('/api/common/menuList_ajax.do');

        if (res && res.menuList) {
          this.setMenuList(res.menuList, options);
        } else {
          console.warn('No menuList found in response from /api/common/menuList_ajax.do', res);
        }
      } catch (error) {
        console.error('Failed to fetch menu from server session:', error);
      }
    },

    // Reset all state (for logout)
    $reset() {
      this.rawMenuData = [];
      this.menuUserAuthorities = [];
      // 다른 회사 계정으로 다시 로그인할 수 있으므로 구독 목록도 비운다
      this.serviceTypes = [];

      // Clear localStorage
      if (import.meta.client) {
        localStorage.removeItem(LOGIN_STORAGE_KEYS.menuListJson);
        localStorage.removeItem(LOGIN_STORAGE_KEYS.loginUserKey);
        localStorage.removeItem('menuServiceTypes');
      }
    },

    // Debug helper
    debugCurrentMenus() {
      console.log('Current menu data:', this.menuSections);
      console.log('Menu data length:', this.menuSections.length);

      const allPaths: string[] = [];
      const extractPaths = (menus: any[]) => {
        for (const menu of menus) {
          if (menu.path) allPaths.push(menu.path);
          if (menu.tabs) {
            menu.tabs.forEach((tab: any) => {
              if (tab.path) allPaths.push(tab.path);
            });
          }
          if (menu.submenus) {
            menu.submenus.forEach((submenu: any) => {
              if (submenu.items) {
                submenu.items.forEach((item: any) => {
                  if (item.path) allPaths.push(item.path);
                  if (item.tabs) {
                    item.tabs.forEach((tab: any) => {
                      if (tab.path) allPaths.push(tab.path);
                    });
                  }
                });
              }
            });
          }
          if (menu.items) {
            menu.items.forEach((item: any) => {
              if (item.path) allPaths.push(item.path);
              if (item.tabs) {
                item.tabs.forEach((tab: any) => {
                  if (tab.path) allPaths.push(tab.path);
                });
              }
            });
          }
        }
      };

      extractPaths(this.menuSections);
      console.log('All paths:', allPaths);
      console.log(
        '/screens/task/collaboration exists:',
        allPaths.includes('/screens/task/collaboration'),
      );
    },
  },
});
